import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus, Search, Pencil, Trash2, MapPin, X, ChevronUp, ChevronDown,
} from 'lucide-react';

const STATUSES = [
  { value: 'for_sale', label: 'For Sale' },
  { value: 'new_build', label: 'New Build' },
  { value: 'under_offer', label: 'Under Offer' },
  { value: 'sold', label: 'Sold' },
];

const PROPERTY_TYPES = [
  'Apartment', 'Villa', 'Townhouse', 'Penthouse', 'Bungalow', 'Duplex', 'Studio',
];

const EMPTY_FORM = {
  slug: '', ref: '', status: 'for_sale', listing_date: '',
  title: '', property_type: 'Apartment', description_html: '',
  price: 0, price_currency: 'EUR',
  location: { area: '', subarea: '', province: 'Alicante', address_line: '', lat: null, lng: null },
  specs: {
    bedrooms: 0, bathrooms: 0, build_size_m2: 0, terrace_m2: null,
    plot_size_m2: null, floor: null, total_floors: null,
    year_built: null, orientation: '', energy_rating: '',
  },
  features: [],
  images: [],
  floor_plan_url: '',
  legal: { catastro_ref: '', energy_cert: '', community_fees: null, ibi_annual: null, basura_annual: null },
  seo: { meta_title: '', meta_description: '', canonical: '' },
  faq: [],
  agent: { name: '', email: '', phone: '', photo: '', role: '', languages: [], bio: '' },
};

export default function SalesPropertiesPage() {
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [newFeature, setNewFeature] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const fetchProperties = useCallback(async () => {
    try {
      setLoading(true);
      const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const { data } = await axios.get(`${API}/sales/`, { params });
      setProperties(data.items || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('Failed to load sales properties');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, page]);

  useEffect(() => { fetchProperties(); }, [fetchProperties]);

  const resetForm = () => {
    setFormData(JSON.parse(JSON.stringify(EMPTY_FORM)));
    setSelectedProperty(null);
    setActiveTab('basic');
  };

  const openEdit = (prop) => {
    setSelectedProperty(prop);
    setFormData({
      ...JSON.parse(JSON.stringify(EMPTY_FORM)),
      ...JSON.parse(JSON.stringify(prop)),
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedProperty) {
        await axios.put(`${API}/sales/${selectedProperty.id}`, formData);
        toast.success('Sales property updated');
      } else {
        await axios.post(`${API}/sales/`, formData);
        toast.success('Sales property created');
      }
      setShowDialog(false);
      resetForm();
      fetchProperties();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save');
    }
  };

  const handleDelete = async () => {
    if (!propertyToDelete) return;
    try {
      await axios.delete(`${API}/sales/${propertyToDelete.id}`);
      toast.success('Sales property deleted');
      setPropertyToDelete(null);
      fetchProperties();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const updateField = (path, value) => {
    setFormData((prev) => {
      const next = { ...prev };
      const parts = path.split('.');
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) {
        obj[parts[i]] = { ...obj[parts[i]] };
        obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const addImage = () => {
    setFormData((prev) => ({
      ...prev,
      images: [...prev.images, { url: '', alt: '' }],
    }));
  };

  const removeImage = (idx) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== idx),
    }));
  };

  const moveImage = (idx, dir) => {
    setFormData((prev) => {
      const imgs = [...prev.images];
      const target = idx + dir;
      if (target < 0 || target >= imgs.length) return prev;
      [imgs[idx], imgs[target]] = [imgs[target], imgs[idx]];
      return { ...prev, images: imgs };
    });
  };

  const addFaq = () => {
    setFormData((prev) => ({
      ...prev,
      faq: [...prev.faq, { question: '', answer: '' }],
    }));
  };

  const removeFaq = (idx) => {
    setFormData((prev) => ({
      ...prev,
      faq: prev.faq.filter((_, i) => i !== idx),
    }));
  };

  const formatPrice = (p) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(p);

  const statusColor = (s) => {
    const m = { for_sale: 'bg-green-100 text-green-800', new_build: 'bg-blue-100 text-blue-800', under_offer: 'bg-yellow-100 text-yellow-800', sold: 'bg-gray-100 text-gray-600' };
    return m[s] || 'bg-gray-100 text-gray-600';
  };

  if (loading && properties.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#0c2431]">Sales Properties</h1>
          <p className="text-[#194a50]/70 mt-1">{total} listings</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search ref, title, city…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              className="pl-10 w-64"
            />
          </div>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(0); }}>
            <SelectTrigger className="w-36"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>

          <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#194a50] hover:bg-[#10555e]">
                <Plus className="w-4 h-4 mr-2" />Add Listing
              </Button>
            </DialogTrigger>

            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
              <DialogHeader>
                <DialogTitle>{selectedProperty ? 'Edit Listing' : 'New Listing'}</DialogTitle>
              </DialogHeader>

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-9 text-xs">
                  <TabsTrigger value="basic">Basic</TabsTrigger>
                  <TabsTrigger value="location">Location</TabsTrigger>
                  <TabsTrigger value="specs">Specs</TabsTrigger>
                  <TabsTrigger value="features">Features</TabsTrigger>
                  <TabsTrigger value="images">Images</TabsTrigger>
                  <TabsTrigger value="content">Content & SEO</TabsTrigger>
                  <TabsTrigger value="legal">Legal</TabsTrigger>
                  <TabsTrigger value="faq">FAQ</TabsTrigger>
                  <TabsTrigger value="agent">Agent</TabsTrigger>
                </TabsList>

                <form onSubmit={handleSubmit} className="mt-4 space-y-4">
                  {/* Basic */}
                  <TabsContent value="basic" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Title</Label><Input value={formData.title} onChange={(e) => updateField('title', e.target.value)} required /></div>
                      <div><Label>Reference</Label><Input value={formData.ref} onChange={(e) => updateField('ref', e.target.value)} required /></div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Status</Label>
                        <Select value={formData.status} onValueChange={(v) => updateField('status', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Property Type</Label>
                        <Select value={formData.property_type} onValueChange={(v) => updateField('property_type', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label>Listing Date</Label><Input type="date" value={formData.listing_date || ''} onChange={(e) => updateField('listing_date', e.target.value)} /></div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div><Label>Price (€)</Label><Input type="number" value={formData.price} onChange={(e) => updateField('price', Number(e.target.value))} /></div>
                      <div><Label>Slug</Label><Input value={formData.slug} onChange={(e) => updateField('slug', e.target.value)} required /></div>
                    </div>
                  </TabsContent>

                  {/* Location */}
                  <TabsContent value="location" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>City (Area)</Label><Input value={formData.location?.area || ''} onChange={(e) => updateField('location.area', e.target.value)} /></div>
                      <div><Label>Neighbourhood (Subarea)</Label><Input value={formData.location?.subarea || ''} onChange={(e) => updateField('location.subarea', e.target.value)} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Province</Label><Input value={formData.location?.province || ''} onChange={(e) => updateField('location.province', e.target.value)} /></div>
                      <div><Label>Address</Label><Input value={formData.location?.address_line || ''} onChange={(e) => updateField('location.address_line', e.target.value)} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Latitude</Label><Input type="number" step="any" value={formData.location?.lat ?? ''} onChange={(e) => updateField('location.lat', e.target.value ? Number(e.target.value) : null)} /></div>
                      <div><Label>Longitude</Label><Input type="number" step="any" value={formData.location?.lng ?? ''} onChange={(e) => updateField('location.lng', e.target.value ? Number(e.target.value) : null)} /></div>
                    </div>
                  </TabsContent>

                  {/* Specs */}
                  <TabsContent value="specs" className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div><Label>Bedrooms</Label><Input type="number" value={formData.specs?.bedrooms ?? 0} onChange={(e) => updateField('specs.bedrooms', Number(e.target.value))} /></div>
                      <div><Label>Bathrooms</Label><Input type="number" value={formData.specs?.bathrooms ?? 0} onChange={(e) => updateField('specs.bathrooms', Number(e.target.value))} /></div>
                      <div><Label>Build Size (m²)</Label><Input type="number" value={formData.specs?.build_size_m2 ?? 0} onChange={(e) => updateField('specs.build_size_m2', Number(e.target.value))} /></div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div><Label>Terrace (m²)</Label><Input type="number" value={formData.specs?.terrace_m2 ?? ''} onChange={(e) => updateField('specs.terrace_m2', e.target.value ? Number(e.target.value) : null)} /></div>
                      <div><Label>Plot Size (m²)</Label><Input type="number" value={formData.specs?.plot_size_m2 ?? ''} onChange={(e) => updateField('specs.plot_size_m2', e.target.value ? Number(e.target.value) : null)} /></div>
                      <div><Label>Year Built</Label><Input type="number" value={formData.specs?.year_built ?? ''} onChange={(e) => updateField('specs.year_built', e.target.value ? Number(e.target.value) : null)} /></div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div><Label>Floor</Label><Input type="number" value={formData.specs?.floor ?? ''} onChange={(e) => updateField('specs.floor', e.target.value ? Number(e.target.value) : null)} /></div>
                      <div><Label>Total Floors</Label><Input type="number" value={formData.specs?.total_floors ?? ''} onChange={(e) => updateField('specs.total_floors', e.target.value ? Number(e.target.value) : null)} /></div>
                      <div><Label>Orientation</Label><Input value={formData.specs?.orientation || ''} onChange={(e) => updateField('specs.orientation', e.target.value)} placeholder="e.g. South" /></div>
                    </div>
                    <div><Label>Energy Rating</Label><Input value={formData.specs?.energy_rating || ''} onChange={(e) => updateField('specs.energy_rating', e.target.value)} placeholder="e.g. E" className="w-32" /></div>
                  </TabsContent>

                  {/* Features */}
                  <TabsContent value="features" className="space-y-4">
                    <div className="flex gap-2">
                      <Input placeholder="Add feature…" value={newFeature} onChange={(e) => setNewFeature(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (newFeature.trim()) { updateField('features', [...formData.features, newFeature.trim()]); setNewFeature(''); } } }}
                      />
                      <Button type="button" variant="outline" onClick={() => { if (newFeature.trim()) { updateField('features', [...formData.features, newFeature.trim()]); setNewFeature(''); } }}>Add</Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.features.map((f, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-[#194a50]/10 text-[#194a50] rounded-full text-sm">
                          {f}
                          <button type="button" onClick={() => updateField('features', formData.features.filter((_, idx) => idx !== i))}><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                    </div>
                  </TabsContent>

                  {/* Images */}
                  <TabsContent value="images" className="space-y-4">
                    <Button type="button" variant="outline" onClick={addImage}><Plus className="w-4 h-4 mr-1" />Add Image</Button>
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {formData.images.map((img, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 border rounded">
                          <span className="text-xs text-gray-400 w-6">{i + 1}</span>
                          {img.url && <img src={img.url} alt="" className="w-12 h-12 object-cover rounded" />}
                          <Input placeholder="Image URL" value={img.url} onChange={(e) => { const imgs = [...formData.images]; imgs[i] = { ...imgs[i], url: e.target.value }; updateField('images', imgs); }} className="flex-1" />
                          <Input placeholder="Alt text" value={img.alt} onChange={(e) => { const imgs = [...formData.images]; imgs[i] = { ...imgs[i], alt: e.target.value }; updateField('images', imgs); }} className="w-40" />
                          <div className="flex flex-col">
                            <button type="button" onClick={() => moveImage(i, -1)} disabled={i === 0}><ChevronUp className="w-4 h-4" /></button>
                            <button type="button" onClick={() => moveImage(i, 1)} disabled={i === formData.images.length - 1}><ChevronDown className="w-4 h-4" /></button>
                          </div>
                          <button type="button" onClick={() => removeImage(i)}><Trash2 className="w-4 h-4 text-red-500" /></button>
                        </div>
                      ))}
                    </div>
                  </TabsContent>

                  {/* Content & SEO */}
                  <TabsContent value="content" className="space-y-4">
                    <div><Label>Description (HTML)</Label><Textarea rows={8} value={formData.description_html} onChange={(e) => updateField('description_html', e.target.value)} /></div>
                    <div><Label>Meta Title</Label><Input value={formData.seo?.meta_title || ''} onChange={(e) => updateField('seo.meta_title', e.target.value)} /></div>
                    <div><Label>Meta Description</Label><Textarea rows={3} value={formData.seo?.meta_description || ''} onChange={(e) => updateField('seo.meta_description', e.target.value)} /></div>
                    <div><Label>Canonical URL</Label><Input value={formData.seo?.canonical || ''} onChange={(e) => updateField('seo.canonical', e.target.value)} /></div>
                  </TabsContent>

                  {/* Legal */}
                  <TabsContent value="legal" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Community Fees (€/month)</Label><Input type="number" value={formData.legal?.community_fees ?? ''} onChange={(e) => updateField('legal.community_fees', e.target.value ? Number(e.target.value) : null)} /></div>
                      <div><Label>IBI Annual (€)</Label><Input type="number" value={formData.legal?.ibi_annual ?? ''} onChange={(e) => updateField('legal.ibi_annual', e.target.value ? Number(e.target.value) : null)} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Basura Annual (€)</Label><Input type="number" value={formData.legal?.basura_annual ?? ''} onChange={(e) => updateField('legal.basura_annual', e.target.value ? Number(e.target.value) : null)} /></div>
                      <div><Label>Catastro Ref</Label><Input value={formData.legal?.catastro_ref || ''} onChange={(e) => updateField('legal.catastro_ref', e.target.value)} /></div>
                    </div>
                    <div><Label>Energy Certificate</Label><Input value={formData.legal?.energy_cert || ''} onChange={(e) => updateField('legal.energy_cert', e.target.value)} /></div>
                  </TabsContent>

                  {/* FAQ */}
                  <TabsContent value="faq" className="space-y-4">
                    <Button type="button" variant="outline" onClick={addFaq}><Plus className="w-4 h-4 mr-1" />Add FAQ</Button>
                    {formData.faq.map((item, i) => (
                      <div key={i} className="p-3 border rounded space-y-2">
                        <div className="flex justify-between items-center">
                          <Label>Question {i + 1}</Label>
                          <button type="button" onClick={() => removeFaq(i)}><Trash2 className="w-4 h-4 text-red-500" /></button>
                        </div>
                        <Input value={item.question} onChange={(e) => { const faqs = [...formData.faq]; faqs[i] = { ...faqs[i], question: e.target.value }; updateField('faq', faqs); }} placeholder="Question" />
                        <Textarea rows={2} value={item.answer} onChange={(e) => { const faqs = [...formData.faq]; faqs[i] = { ...faqs[i], answer: e.target.value }; updateField('faq', faqs); }} placeholder="Answer" />
                      </div>
                    ))}
                  </TabsContent>

                  {/* Agent */}
                  <TabsContent value="agent" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Name</Label><Input value={formData.agent?.name || ''} onChange={(e) => updateField('agent.name', e.target.value)} /></div>
                      <div><Label>Role</Label><Input value={formData.agent?.role || ''} onChange={(e) => updateField('agent.role', e.target.value)} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Email</Label><Input type="email" value={formData.agent?.email || ''} onChange={(e) => updateField('agent.email', e.target.value)} /></div>
                      <div><Label>Phone</Label><Input value={formData.agent?.phone || ''} onChange={(e) => updateField('agent.phone', e.target.value)} /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Photo URL</Label><Input value={formData.agent?.photo || ''} onChange={(e) => updateField('agent.photo', e.target.value)} /></div>
                      <div><Label>Languages (comma-separated)</Label><Input value={(formData.agent?.languages || []).join(', ')} onChange={(e) => updateField('agent.languages', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} /></div>
                    </div>
                    <div><Label>Bio</Label><Textarea rows={3} value={formData.agent?.bio || ''} onChange={(e) => updateField('agent.bio', e.target.value)} /></div>
                  </TabsContent>

                  <div className="flex justify-end gap-3 pt-6 border-t mt-6">
                    <Button type="button" variant="outline" onClick={() => { setShowDialog(false); resetForm(); }}>Cancel</Button>
                    <Button type="submit" className="bg-[#194a50] hover:bg-[#10555e]">{selectedProperty ? 'Update' : 'Create'}</Button>
                  </div>
                </form>
              </Tabs>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Table */}
      {properties.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-500">No sales properties found.</CardContent></Card>
      ) : (
        <div className="bg-white rounded-lg border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Ref</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Title</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">City</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Price</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600">Beds</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600">m²</th>
                <th className="text-center px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {properties.map((p) => (
                <tr key={p.id || p.ref} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs">{p.ref}</td>
                  <td className="px-4 py-3 max-w-xs truncate">{p.title}</td>
                  <td className="px-4 py-3"><span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{p.location?.area}</span></td>
                  <td className="px-4 py-3 text-right font-medium">{formatPrice(p.price)}</td>
                  <td className="px-4 py-3 text-center">{p.specs?.bedrooms ?? '–'}</td>
                  <td className="px-4 py-3 text-center">{p.specs?.build_size_m2 ?? '–'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(p.status)}`}>
                      {STATUSES.find((s) => s.value === p.status)?.label || p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(p)}><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setPropertyToDelete(p)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {total > PAGE_SIZE && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <span className="text-sm text-gray-500">Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</Button>
                <Button size="sm" variant="outline" disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(page + 1)}>Next</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!propertyToDelete} onOpenChange={(open) => { if (!open) setPropertyToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Sales Property</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{propertyToDelete?.ref}</strong> — {propertyToDelete?.title}? This will soft-delete the listing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
